import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { GoBackLink } from '../GoBackLink'
import { Table } from 'react-bootstrap'
import { Switch } from '@CommonComponents/buttons'
import { useStateValue } from '@State'
import { client } from '@Utils'
import { toast } from '@CommonComponents/toast'
import { RemoveConfirmModal } from '../modals/RemoveConfirmModal'

const DeploymentsSection = ({
  title,
  deployments,
  match,
  isPrivileged,
  openModal,
}) => (
  <div className="card-myed">
    <h3>{title}:</h3>
    <div className="table-responsive">
      <Table striped bordered className="table-sm" hover>
        <thead>
          <tr>
            <th>Id</th>
            <th>Deployment Id</th>
            <th>Name</th>
            <th style={{ minWidth: 90 }}>Initialization Date</th>
            <th>View</th>
            {isPrivileged && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {deployments &&
            deployments.map(deployment => (
              <tr key={deployment.id}>
                <td>{deployment.id}</td>
                <td>{deployment.deploymentid}</td>
                <td>{deployment.data.name}</td>
                <td>{new Date(deployment.created).toLocaleDateString()}</td>
                <td>
                  <Link
                    to={`/account/admin_lti13/-/platforms/${match.params.lmsCode}/issuers/${match.params.issuerId}/clients/${match.params.clientId}/deployments/${deployment.id}`}
                  >
                    View Deployment
                  </Link>
                </td>
                {isPrivileged && (
                  <td>
                    <a
                      onClick={() => openModal(deployment)}
                      className="btn btn-sm btn-sm-myed btn-danger"
                    >
                      Remove <i className="fa-regular fa-window-close" />
                    </a>
                  </td>
                )}
              </tr>
            ))}
        </tbody>
      </Table>
    </div>
  </div>
)

// Deployments aren't a local model — they're fetched live from the external
// Darin/dserv LTI-bridge service (see DarinClient.get_deployments()), whose
// schema has no test/sandbox/environment flag to filter on. Exact-name
// matching is the only mechanism available today, so a new test/sandbox
// deployment whose name doesn't match one of these strings exactly will
// silently land in "Production Deployments" instead. Keep this list in sync
// by hand whenever a new non-production deployment is added upstream.
const TEST_DEPLOYMENT_NAMES = [
  'MyEducator Instance Install',
  'MyEducator (Course Install)',
  'University of Pittsburgh (Sandbox)',
]

export const Client = ({ match }) => {
  const [currentState, dispatch] = useStateValue()
  const { text } = window.MEButtons

  const [removeContextModalOpen, toggleRemoveModal] = useState(false)
  const [removeContextURL, setRemoveContextURL] = useState('')
  const [removeContextMessage, setRemoveContextMessage] = useState('')

  useEffect(() => {
    const getContexts = async () => {
      try {
        let resp = await client.get(
          `/account/admin_lti13.getDeployments/-/${match.params.issuerId}/${match.params.clientId}/`
        )
        dispatch({ type: 'updateMessage', message: null })
        dispatch({
          type: 'updateDeploymentsTable',
          deploymentsTableData: resp.data.deployments,
        })
        dispatch({ type: 'updateIssuer', issuer: resp.data.issuer })
        dispatch({ type: 'updateClient', client: resp.data.client })
      } catch (err) {
        if (err.response) {
          if (err.response.status > 205) {
            try {
              dispatch({
                type: 'updateMessage',
                message: `${err.response.statusText}: ${err.response.data.error}`,
              })
            } catch (e) {
              dispatch({
                type: 'updateMessage',
                message: err.response.statusText,
              })
            }
          } else {
            dispatch({ type: 'updateMessage', message: err })
          }
        } else {
          dispatch({ type: 'updateMessage', message: err })
        }
      }
    }
    getContexts()
  }, [currentState.reloadSwitch])

  const openModal = contextObject => {
    setRemoveContextURL(
      `/account/admin_lti13.deleteDeployment/-/${contextObject.id}/`
    )
    setRemoveContextMessage(
      `Are you sure you want to remove Deployment: ${contextObject.data.name}?`
    )
    toggleRemoveModal(true)
  }

  return (
    <div className="container" style={{ width: '100%' }}>
      <div className="mb-4 go-back-button-left-align">
        {currentState.isPrivileged && (
          <GoBackLink to={`/account/admin_lti13/-/platforms/${match.params.lmsCode}/issuers/${match.params.issuerId}`} />
        )}
      </div>
      <div className="card-myed">
        <h3>Client:</h3>
        {currentState.issuer && currentState.client && (
          <div>
            {currentState.isPrivileged && (
              <div className="pull-right">
                <Switch
                  label="Enable LTI 1.3 Connection"
                  data-busy-label={text('strSaving')}
                  data-success-label={text('strChangesSaved')}
                  data-failure-label={text('strSaveFailed')}
                  data-failure-text={text('strTryAgain')}
                  onChange={async () => {
                    let data = {
                      lti_login: !currentState.client.data.lti_login,
                    }
                    try {
                      let resp = await client.post(
                        `/account/admin_lti13.lti_login/-/${match.params.issuerId}/${match.params.clientId}`,
                        data
                      )
                      dispatch({
                        type: 'updateClient',
                        client: resp.data.client,
                      })
                      if (resp.data.client.data.lti_login) {
                        toast.success(
                          'LTI Login Status Updated! LTI login is now Enabled'
                        )
                      } else {
                        toast.warn(
                          'LTI Login Status Updated! LTI login is now Disabled'
                        )
                      }
                    } catch (err) {
                      toast.error(
                        `There was an error trying to toggle the Login status: ${err}`
                      )
                      throw err
                    }
                  }}
                  checked={!!currentState.client.data.lti_login}
                />
              </div>
            )}
            <p>
              <span style={{ fontSize: '1.2em', fontWeight: 'bolder' }}>
                ID:
              </span>{' '}
              {currentState.client.id}
            </p>
            <p>
              <span style={{ fontSize: '1.2em', fontWeight: 'bolder' }}>
                Name:
              </span>{' '}
              {currentState.client.data.name}
            </p>
            <p>
              <span style={{ fontSize: '1.2em', fontWeight: 'bolder' }}>
                Connection Enabled:{' '}
              </span>{' '}
              {currentState.client.data.lti_login ? 'Yes' : 'No'}
            </p>
            <p>
              <span style={{ fontSize: '1.2em', fontWeight: 'bolder' }}>
                Issuer:{' '}
              </span>{' '}
              {`${currentState.issuer.data.name} (${currentState.issuer.iss}) [${currentState.issuer.id}]`}
            </p>
          </div>
        )}
      </div>
      <DeploymentsSection
        title="Production Deployments"
        deployments={
          currentState.deploymentsTableData
            ? currentState.deploymentsTableData.filter(
                d => !TEST_DEPLOYMENT_NAMES.includes(d.data.name)
              )
            : null
        }
        match={match}
        isPrivileged={currentState.isPrivileged}
        openModal={openModal}
      />
      <DeploymentsSection
        title="Test Deployments (Internal, External, and Sandbox)"
        deployments={
          currentState.deploymentsTableData
            ? currentState.deploymentsTableData.filter(d =>
                TEST_DEPLOYMENT_NAMES.includes(d.data.name)
              )
            : null
        }
        match={match}
        isPrivileged={currentState.isPrivileged}
        openModal={openModal}
      />
      <RemoveConfirmModal
        modalTitle="Remove Context"
        message={removeContextMessage}
        endpoint={removeContextURL}
        show={removeContextModalOpen}
        toggle={toggleRemoveModal}
        currentState={currentState}
        dispatch={dispatch}
      />
    </div>
  )
}
